import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { map, orderBy, has, set, get, split } from 'lodash/fp';
import { Table, Icon, IconButton } from 'rsuite';
import Pagination from '../../Pagination';
import DateCell from '../../widgets/custom-cell/Date';
import * as PERMISSIONS from '../../../constants/permissions/inventory';
import { fullAccess, modifyAccess } from '../../../helpers/permission';
import AccessControl from '../../AccessControl';
import { inventoryEditLocationPath } from '../../../routes/Paths';
import IconLink from '../../IconLink';
import DeleteConfirmationModal from '../../DeleteConfirmationModal';
import PaginationType from '../../../interfaces/PaginationType';
import CustomHeaderCell from '../../widgets/custom-cell/HeaderCell';
import { InventoryLocationType } from '../../../interfaces/inventory';

const { Column, HeaderCell, Cell } = Table;
const ActionCell = ({ rowData, dataKey, handleDelete, ...props }) => (
  <Cell {...props} className="link-group">
    <AccessControl allowOnlyFor={modifyAccess(PERMISSIONS.LOCATION)}>
      <IconLink
        icon="edit2"
        to={{
          pathname: inventoryEditLocationPath.replace(':id', rowData.id),
          params: { id: rowData.id }
        }}
      />
    </AccessControl>
    <AccessControl allowOnlyFor={fullAccess(PERMISSIONS.LOCATION)}>
      <IconButton
        style={{ top: '-8px' }}
        appearance="link"
        color="red"
        onClick={() => handleDelete(rowData)}
        icon={<Icon icon="trash" />}
      />
    </AccessControl>
  </Cell>
);

ActionCell.propTypes = {
  rowData: InventoryLocationType,
  dataKey: PropTypes.string.isRequired,
  handleDelete: PropTypes.func.isRequired
};
ActionCell.defaultProps = {
  rowData: null
};

const ZonesCell = ({ rowData, dataKey, ...props }) => (
  <Cell {...props} className="link-group">
    <div style={{ whiteSpace: 'normal' }}>
      {map('name', rowData[dataKey]).join(', ')}
    </div>
  </Cell>
);

ZonesCell.propTypes = {
  rowData: InventoryLocationType,
  dataKey: PropTypes.string.isRequired
};
ZonesCell.defaultProps = {
  rowData: null
};

class InventoryLocationList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      locationToDelete: null,
      showConfirmDelete: false
    };
  }

  componentDidMount() {
    const { onInit } = this.props;
    onInit();
  }

  componentDidUpdate({ isDeleted: prevIsDeleted, isDeleting: prevIsDeleting }) {
    const { isDeleted, isDeleting } = this.props;
    const deleteSucceed = !prevIsDeleted && isDeleted;
    const deleteFailed = prevIsDeleting && !isDeleting && !isDeleted;
    if (deleteSucceed || deleteFailed) {
      this.handleHideDeleteModal();
    }
  }

  getData() {
    const { locations } = this.props;
    const { sortColumn, sortType } = this.state;
    return orderBy(sortColumn, sortType, locations);
  }

  handleSortColumn = (sortColumn, sortType) => {
    const { onSort, query } = this.props;
    onSort(sortColumn, sortType, query);
  };

  handleOpenDeleteModal = locationToDelete => {
    this.setState({ showConfirmDelete: true, locationToDelete });
  };

  handleHideDeleteModal = () => {
    this.setState({ showConfirmDelete: false });
  };

  handlConfirmDelete = () => {
    const { locationToDelete } = this.state;
    const { onDelete } = this.props;
    onDelete(locationToDelete);
  };

  handleChangePage = page => {
    const { onChangePage, loadedPages, query } = this.props;
    const isPageLoaded = has(page, loadedPages);
    const nextPageQuery = set('page', page, query);
    onChangePage(nextPageQuery, isPageLoaded);
  };

  handleChangeLimit = limit => {
    const { onChangeLimit, query } = this.props;
    const newQuery = { ...query, limit, page: 1 };
    onChangeLimit(newQuery);
  };

  renderPagination() {
    const { pagination } = this.props;
    return (
      <Pagination
        page={pagination.currentPage}
        limit={pagination.limit}
        total={pagination.totalCount}
        onChangePage={this.handleChangePage}
        onChangeLimit={this.handleChangeLimit}
      />
    );
  }

  render() {
    const { isFetching, isDeleting, query } = this.props;
    const [sortColumn, sortType] = split(':', get('order', query));
    const { locationToDelete, showConfirmDelete } = this.state;
    return (
      <Fragment>
        <Table
          height={420}
          loading={isFetching}
          data={this.getData()}
          sortColumn={sortColumn}
          sortType={sortType}
          onSortColumn={this.handleSortColumn}
          bordered
        >
          <Column width={50} align="center" fixed sortable>
            <CustomHeaderCell>Id</CustomHeaderCell>
            <Cell dataKey="id" />
          </Column>
          <Column width={150} sortable>
            <CustomHeaderCell>Name</CustomHeaderCell>
            <Cell dataKey="name" />
          </Column>
          <Column width={250}>
            <CustomHeaderCell>Zones</CustomHeaderCell>
            <ZonesCell dataKey="zones" />
          </Column>
          <Column width={140} sortable>
            <CustomHeaderCell>Date Created</CustomHeaderCell>
            <DateCell dataKey="createdAt" />
          </Column>
          <Column width={140} sortable>
            <CustomHeaderCell>Date Updated</CustomHeaderCell>
            <DateCell dataKey="updatedAt" />
          </Column>
          <Column width={100}>
            <HeaderCell align="center">Action</HeaderCell>
            <ActionCell
              align="center"
              dataKey="id"
              handleDelete={this.handleOpenDeleteModal}
            />
          </Column>
        </Table>

        {this.renderPagination()}

        {locationToDelete && (
          <DeleteConfirmationModal
            message={`Are you sure you want to delete ${
              locationToDelete.name
            }?`}
            show={showConfirmDelete}
            onHide={this.handleHideDeleteModal}
            onConfirm={this.handlConfirmDelete}
            location={locationToDelete}
            isDeleting={isDeleting}
          />
        )}
      </Fragment>
    );
  }
}

InventoryLocationList.propTypes = {
  locations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isFetching: PropTypes.bool.isRequired,
  isDeleting: PropTypes.bool.isRequired,
  isDeleted: PropTypes.bool.isRequired,
  onInit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  query: PropTypes.shape({}).isRequired,
  loadedPages: PropTypes.shape({}).isRequired,
  pagination: PaginationType.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onChangeLimit: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired
};

export default InventoryLocationList;
